import { useParams, Link } from "react-router-dom";
import { useLocalePrefix } from "@/hooks/useLocalePrefix";
import { deepWithStats } from "@/data/modelStats";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { trackBlogView } from "@/lib/analytics";
import { useEffect } from "react";
import PageLayout from "@/components/PageLayout";

const BlogPostPage = () => {
  // `lng` is present on the per-locale route (/:lng/blog/:slug), absent on the
  // French root route (/blog/:slug). It drives the canonical + back-link prefix
  // so a localized post self-canonicalises to /{lng}/blog/{slug}, matching the
  // prerendered URL (see scripts/prerender-seo.js).
  const { slug } = useParams();
  const localePrefix = useLocalePrefix();
  useEffect(() => {
    if (slug) trackBlogView(slug);
  }, [slug]);
  const { t } = useTranslation();
  const rawPosts = deepWithStats(t("blog.posts", { returnObjects: true }));
  const posts = Array.isArray(rawPosts) ? rawPosts : [];
  const post = posts.find((p) => p.slug === slug);

  if (!post)
    return (
      <PageLayout>
        <div className="text-center py-40 text-slate-400">
          Article introuvable
        </div>
      </PageLayout>
    );

  return (
    <PageLayout>
      <SEO
        title={post.title}
        description={post.excerpt}
        canonical={`${localePrefix}/blog/${encodeURIComponent(slug)}`}
      />
      <article className="max-w-3xl mx-auto px-4 py-16">
        <Link
          to={`${localePrefix}/blog`}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> {t("blog.backToList")}
        </Link>
        <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
          {post.category}
        </span>
        <h1 className="font-heading text-4xl font-bold text-white mt-4 mb-3">
          {post.title}
        </h1>
        <div className="flex items-center gap-4 text-slate-400 text-sm mb-10">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {post.date}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {post.readTime}
          </span>
        </div>

        <div className="prose prose-invert max-w-none">
          {(post.sections || []).map((s, i) => (
            <section key={i} className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-3">
                {s.title}
              </h2>
              <p className="text-slate-400 leading-relaxed">{s.content}</p>
            </section>
          ))}
        </div>

        <div className="glass rounded-2xl p-8 text-center mt-12">
          <h2 className="font-heading text-2xl font-bold text-white mb-3">
            {t("blog.ctaTitle")}
          </h2>
          <p className="text-slate-400 mb-6">{t("blog.ctaSubtitle")}</p>
          <Link to="/register">
            <Button
              size="lg"
              className="btn-glow bg-primary-strong hover:bg-primary-strong/90"
            >
              {t("landing.cta")} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </article>
    </PageLayout>
  );
};
export default BlogPostPage;
